import api from './api';

/**
 * Uma entidade, COMO A API A DEVOLVE.
 *
 * Esta interface descrevia outra coisa — `name`, `status: 'active' | 'inactive'`,
 * `createdAt` — e a API nunca devolveu esses campos. `getEntities` faz
 * `return response.data` sem converter nada, logo o que chega é `desig`, `estado`
 * e `dataRegisto`.
 *
 * O tipo estava a prometer campos inexistentes, e o TypeScript não podia avisar:
 * quem confiasse nele escrevia `e.name` e recebia `undefined` em silêncio. Foi
 * exactamente isso que deixou o selector de promotores de /contratos a mostrar uma
 * lista de itens sem texto — os dados estavam lá, os rótulos vinham vazios.
 *
 * O Events.tsx contornava com `(e: any) => e.desig`. Um `any` a tapar um tipo errado
 * esconde o problema para o próximo.
 *
 * Corrigido a 07/08/2026 para os nomes reais. Se a API mudar, muda-se aqui.
 */
export interface Entity {
  id: string;
  codigo?: string;
  /** Designação — o nome da entidade. NÃO existe campo `name`. */
  desig: string;
  nomeContacto?: string | null;
  morada?: string | null;
  numConta?: string | null;
  url?: string | null;
  /** 'A' = activa. NÃO é 'active'/'inactive'. */
  estado?: string;
  dataRegisto?: string | null;
}

export interface EntityFilters {
  search?: string;
  status?: string;
  page?: number;
  limit?: number;
}

export interface EntitiesResponse {
  success: boolean;
  data: Entity[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

class EntitiesService {
  /**
   * Get all entities with filters
   */
  async getEntities(filters?: EntityFilters): Promise<EntitiesResponse> {
    try {
      const params = new URLSearchParams();
      if (filters?.search) params.append('search', filters.search);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());

      const response = await api.get(`/admin/entities?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get entities error:', error);
      throw error;
    }
  }

  /**
   * Get entity by ID
   */
  async getEntity(id: string): Promise<Entity> {
    try {
      const response = await api.get(`/admin/entities/${id}`);
      return response.data.data;
    } catch (error) {
      console.error('Get entity error:', error);
      throw error;
    }
  }

  /**
   * Create new entity
   */
  async createEntity(data: Partial<Entity>): Promise<Entity> {
    try {
      const response = await api.post('/admin/entities', data);
      return response.data.data;
    } catch (error) {
      console.error('Create entity error:', error);
      throw error;
    }
  }

  /**
   * Update entity
   */
  async updateEntity(id: string, data: Partial<Entity>): Promise<Entity> {
    try {
      const response = await api.put(`/admin/entities/${id}`, data);
      return response.data.data;
    } catch (error) {
      console.error('Update entity error:', error);
      throw error;
    }
  }

  /**
   * Delete entity
   */
  async deleteEntity(id: string): Promise<void> {
    try {
      await api.delete(`/admin/entities/${id}`);
    } catch (error) {
      console.error('Delete entity error:', error);
      throw error;
    }
  }

  /**
   * Activate entity
   */
  async activateEntity(id: string): Promise<Entity> {
    try {
      const response = await api.post(`/admin/entities/${id}/activate`);
      return response.data.data;
    } catch (error) {
      console.error('Activate entity error:', error);
      throw error;
    }
  }

  /**
   * Deactivate entity
   */
  async deactivateEntity(id: string): Promise<Entity> {
    try {
      const response = await api.post(`/admin/entities/${id}/deactivate`);
      return response.data.data;
    } catch (error) {
      console.error('Deactivate entity error:', error);
      throw error;
    }
  }
}

export default new EntitiesService();
